import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  createAppointment,
  fetchAvailability,
  newIdempotencyKey,
} from "../services/bookingService";
import { fetchClients } from "../services/clientService";
import { fetchProviders } from "../services/providerService";
import { fetchCatalogue } from "../services/serviceService";
import { can } from "../services/permissions";
import { getCurrentUser } from "../services/authService";
import { inZone } from "../services/datetime";

/**
 * BIZ-13 — take a booking, including the walk-in nobody has met before.
 *
 * The flow is deliberately short: pick a service, pick a time from what the
 * server says is actually free, say who it is for, book. The availability list
 * is never guessed at locally — every slot shown came from the engine, and the
 * engine is asked again when the booking is taken.
 *
 * Two ways to name the customer, matching what the API accepts: an existing
 * client, or a name and number typed straight in. A phone that is already known
 * quietly resolves to the same client rather than making a second record.
 *
 * ## Two callers, two shapes
 *
 * The diary already knows the branch and its providers and passes both. A
 * client's profile (BIZ-16) knows neither — it knows only *who* — so it passes
 * `branches` and a `client`, and the modal picks the branch itself and loads
 * that branch's providers. Booking from a profile never re-asks who it is for.
 */
export default function BookingModal({
  open,
  branchId,
  branchName,
  branches = null,
  providers = [],
  client = null,
  initial = null,
  onClose,
  onBooked,
}) {
  const { t } = useTranslation();

  // Set only when the caller handed over a list of branches rather than one
  // branch — see the note above.
  const [pickedBranchId, setPickedBranchId] = useState("");
  const [branchProviders, setBranchProviders] = useState([]);
  const choosingBranch = Array.isArray(branches);

  const effectiveBranchId = choosingBranch ? pickedBranchId : branchId;
  const effectiveProviders = choosingBranch ? branchProviders : providers;

  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotZone, setSlotZone] = useState("UTC");
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [chosen, setChosen] = useState(null);

  const [mode, setMode] = useState("new");
  const [clientId, setClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [walkIn, setWalkIn] = useState({ name: "", phone: "", email: "" });

  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("confirmed");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // One key per attempt, not per request: a retry of *this* booking must reuse
  // it, which is the whole point of the header.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const user = getCurrentUser();
  const mayOverridePrice = can(user, "appointments.override_price");

  /* ------------------------------------------------------------ open / reset */

  useEffect(() => {
    if (!open) return;

    setServiceId("");
    setStaffId(initial?.staffId ?? "");
    setDate(initial?.date ?? "");
    setChosen(initial?.time ? { local: initial.time } : null);
    setSlots([]);
    // Booking from a profile: the client is already known, so the picker is
    // skipped entirely rather than prefilled and left editable.
    setMode(client ? "existing" : "new");
    setClientId(client?.id ?? "");
    setClientSearch(client?.name ?? "");
    setClientResults([]);
    setWalkIn({ name: "", phone: "", email: "" });
    setPrice("");
    setNotes("");
    setStatus("confirmed");
    setError("");
    setFieldErrors({});
    setIdempotencyKey(newIdempotencyKey());

    // Nothing can be booked without a branch, so the first one is picked for
    // the caller that did not name one.
    if (choosingBranch) setPickedBranchId(branches[0]?.id ?? "");
  }, [open, initial, client, choosingBranch, branches]);

  useEffect(() => {
    if (!open) return;

    fetchCatalogue({ perPage: 100 })
      .then(({ services: list }) => setServices(list.filter((s) => s.is_active)))
      .catch(() => setServices([]));
  }, [open]);

  // Only the branch's own providers may be offered — the engine refuses the
  // rest, and a name in the list that cannot take the booking is a trap.
  useEffect(() => {
    if (!open || !choosingBranch || !pickedBranchId) {
      return;
    }

    fetchProviders({ perPage: 100, filters: { branch: pickedBranchId } })
      .then(({ providers: list }) => setBranchProviders(list))
      .catch(() => setBranchProviders([]));
  }, [open, choosingBranch, pickedBranchId]);

  /* ------------------------------------------------------------ availability */

  const loadSlots = useCallback(async () => {
    if (!effectiveBranchId || !serviceId || !date) {
      setSlots([]);
      return;
    }

    setSlotsLoading(true);
    try {
      const { slots: list, meta } = await fetchAvailability(effectiveBranchId, serviceId, date, {
        staffId: staffId || null,
      });
      setSlots(list);
      // The branch's own zone, not the browser's: a slot at 10:00 in Riyadh must
      // read as 10:00 on a laptop in London.
      setSlotZone(meta?.timezone ?? "UTC");
    } catch (err) {
      setSlots([]);
      setError(apiErrorMessage(err, t("appointments.availabilityFailed")));
    } finally {
      setSlotsLoading(false);
    }
  }, [effectiveBranchId, serviceId, date, staffId, t]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  /* ----------------------------------------------------------------- clients */

  useEffect(() => {
    // `client` means the caller already said who this is for, so the box that
    // would search for one is not on screen and must not be queried behind it.
    if (client || !open || mode !== "existing" || clientSearch.trim().length < 2) {
      setClientResults([]);
      return undefined;
    }

    // Debounced: reception types fast, and every keystroke is a query.
    const timer = setTimeout(() => {
      fetchClients({ perPage: 10, filters: { search: clientSearch.trim() } })
        .then(({ clients }) => setClientResults(clients))
        .catch(() => setClientResults([]));
    }, 300);

    return () => clearTimeout(timer);
  }, [client, open, mode, clientSearch]);

  /**
   * Slots arrive as UTC instants. Both the label and the value sent back must be
   * the branch's wall clock — never the browser's, which is a different time
   * for anybody not sitting in the branch.
   */
  const localTimes = useMemo(
    () =>
      slots.map((slot) => ({
        value: slot,
        label: inZone(slot.starts_at, slotZone).time,
      })),
    [slots, slotZone]
  );

  if (!open) return null;

  const chosenTime = chosen?.local ?? null;

  const submit = async (e) => {
    e.preventDefault();

    if (!serviceId || !date || !chosenTime) {
      setError(t("appointments.pickSlotFirst"));
      return;
    }

    setSaving(true);
    setError("");
    setFieldErrors({});

    const values = {
      branchId: effectiveBranchId,
      status,
      source: "portal",
      notes,
      services: [
        {
          serviceId,
          staffId: chosen?.staffId || staffId || null,
          startsAt: `${date} ${chosenTime}`,
          ...(mayOverridePrice && price !== "" ? { price } : {}),
        },
      ],
    };

    if (mode === "existing") {
      values.clientId = clientId;
    } else {
      values.client = walkIn;
    }

    try {
      const appointment = await createAppointment(values, idempotencyKey);
      onBooked?.(appointment);
      onClose();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));

      // 409 is not a broken request — the slot went to somebody else between
      // being offered and being taken. Refresh what is free and say so.
      if (err.response?.status === 409) {
        setError(t("appointments.slotTaken"));
        // A new attempt is a new operation, so it needs its own key.
        setIdempotencyKey(newIdempotencyKey());
        setChosen(null);
        loadSlots();
      } else {
        setError(apiErrorMessage(err, t("appointments.bookFailed")));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-2xl p-5">
        <h2 className="mb-1 text-lg font-bold">{t("appointments.newTitle")}</h2>
        {branchName && <p className="mb-4 text-sm text-ink-2">{branchName}</p>}

        <form onSubmit={submit} className="space-y-4">
          {/* Only asked for when the caller did not already know it. */}
          {choosingBranch && (
            <div>
              <label className="field-label" htmlFor="booking-branch">
                {t("appointments.branch")} <span className="text-danger-text">*</span>
              </label>
              <select
                id="booking-branch"
                className="field-input"
                value={pickedBranchId}
                onChange={(e) => {
                  setPickedBranchId(e.target.value);
                  // A different branch has different people and different free
                  // time, so neither choice survives the move.
                  setStaffId("");
                  setChosen(null);
                }}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* What and who with */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="booking-service">
                {t("appointments.service")} <span className="text-danger-text">*</span>
              </label>
              <select
                id="booking-service"
                className="field-input"
                value={serviceId}
                onChange={(e) => {
                  setServiceId(e.target.value);
                  setChosen(null);
                }}
              >
                <option value="">{t("appointments.pickService")}</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} · {service.duration}m
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor="booking-staff">
                {t("appointments.provider")}
              </label>
              <select
                id="booking-staff"
                className="field-input"
                value={staffId}
                onChange={(e) => {
                  setStaffId(e.target.value);
                  setChosen(null);
                }}
              >
                <option value="">{t("appointments.anyProvider")}</option>
                {effectiveProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.display_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* When */}
          <div>
            <label className="field-label" htmlFor="booking-date">
              {t("appointments.date")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="booking-date"
              type="date"
              className="field-input"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setChosen(null);
              }}
            />
          </div>

          {/* Slots — every one of these came from the engine. */}
          {serviceId && date && (
            <div>
              <p className="field-label">{t("appointments.availableSlots")}</p>

              {slotsLoading ? (
                <p className="py-3 text-sm text-ink-2">{t("common.loading")}</p>
              ) : localTimes.length === 0 ? (
                <p className="rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
                  {t("appointments.noSlots")}
                </p>
              ) : (
                <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                  {localTimes.map(({ value, label }) => {
                    const active = chosenTime === label;

                    return (
                      <button
                        key={value.starts_at}
                        type="button"
                        onClick={() =>
                          setChosen({
                            local: label,
                            // Whoever the engine says can take it; with "any
                            // provider" that is the first free person.
                            staffId: staffId || value.staff_ids?.[0] || null,
                          })
                        }
                        className={`rounded-lg border px-3 py-1 text-sm ${
                          active
                            ? "border-accent bg-accent-wash text-accent-text"
                            : "border-line text-ink"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Who for. Already answered when this was opened from a profile. */}
          {client ? (
            <div className="rounded-lg border border-line px-3 py-2 text-sm">
              <span className="text-ink-2">{t("appointments.client.bookingFor")} </span>
              <span className="font-medium">{client.name}</span>
              {client.phone_national && (
                <span className="ms-2 text-xs text-ink-2" dir="ltr">
                  {client.phone_national}
                </span>
              )}
            </div>
          ) : (
          <div className="rounded-lg border border-line p-3">
            <div className="mb-2 flex gap-2">
              {["new", "existing"].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setMode(option)}
                  className={`rounded-full px-3 py-1 text-sm ${
                    mode === option
                      ? "bg-accent-wash text-accent-text"
                      : "bg-raised text-ink-2"
                  }`}
                >
                  {t(`appointments.client.${option}`)}
                </button>
              ))}
            </div>

            {mode === "new" ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="field-label" htmlFor="booking-client-name">
                    {t("appointments.client.name")} <span className="text-danger-text">*</span>
                  </label>
                  <input
                    id="booking-client-name"
                    className="field-input"
                    value={walkIn.name}
                    onChange={(e) => setWalkIn({ ...walkIn, name: e.target.value })}
                  />
                  {fieldErrors["client.name"] && (
                    <p className="field-error">{fieldErrors["client.name"][0]}</p>
                  )}
                </div>
                <div>
                  <label className="field-label" htmlFor="booking-client-phone">
                    {t("appointments.client.phone")}
                  </label>
                  <input
                    id="booking-client-phone"
                    className="field-input"
                    dir="ltr"
                    value={walkIn.phone}
                    onChange={(e) => setWalkIn({ ...walkIn, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="booking-client-email">
                    {t("appointments.client.email")}
                  </label>
                  <input
                    id="booking-client-email"
                    className="field-input"
                    dir="ltr"
                    value={walkIn.email}
                    onChange={(e) => setWalkIn({ ...walkIn, email: e.target.value })}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="field-label" htmlFor="booking-client-search">
                  {t("appointments.client.search")}
                </label>
                <input
                  id="booking-client-search"
                  className="field-input"
                  placeholder={t("appointments.client.searchPlaceholder")}
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />

                {clientResults.length > 0 && (
                  <ul className="mt-2 max-h-32 divide-y divide-line overflow-y-auto rounded-lg border border-line">
                    {clientResults.map((result) => (
                      <li key={result.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setClientId(result.id);
                            setClientSearch(result.name);
                            setClientResults([]);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-start text-sm hover:bg-raised ${
                            clientId === result.id ? "bg-accent-wash" : ""
                          }`}
                        >
                          <span>{result.name}</span>
                          <span className="text-xs text-ink-2" dir="ltr">
                            {result.phone_national ?? result.phone}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {fieldErrors.client_id && (
                  <p className="field-error">{fieldErrors.client_id[0]}</p>
                )}
              </div>
            )}
          </div>
          )}

          {/* Terms */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="booking-status">
                {t("appointments.bookAs")}
              </label>
              <select
                id="booking-status"
                className="field-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="confirmed">{t("appointments.status.confirmed")}</option>
                <option value="pending">{t("appointments.status.pending")}</option>
              </select>
            </div>

            {/* Hidden without the permission; the API drops it regardless. */}
            {mayOverridePrice && (
              <div>
                <label className="field-label" htmlFor="booking-price">
                  {t("appointments.priceOverride")}
                </label>
                <input
                  id="booking-price"
                  type="number"
                  step="0.01"
                  min="0"
                  className="field-input"
                  placeholder={t("appointments.priceOverrideHint")}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
            )}
          </div>

          <div>
            <label className="field-label" htmlFor="booking-notes">
              {t("appointments.notes")}
            </label>
            <textarea
              id="booking-notes"
              rows={2}
              className="field-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && <p className="field-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving || !chosenTime}>
              {saving ? t("common.saving") : t("appointments.book")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
